import React from 'react';

interface ProfileData {
  fullName: string;
  phone: string;
  profileImage: File | null;
  profileImageUrl?: string;
  businessName: string;
  licenseNumber: string;
  yearsOfExperience: number;
  bio: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;
  specialties: string[];
  services: string[];
  treatmentCategories: string[];
  pricingRange: string;
  virtualConsultations: boolean;
  education: Array<{
    degree: string[];
    institution: string;
    year: string;
  }>;
  certifications: Array<{
    name: string[];
    issuer: string;
    year: string;
  }>;
  affiliations: string[];
  insuranceAccepted: string[];
  languagesSpoken: string[];
}

interface Step6ReviewProps {
  data: ProfileData;
}

const Step6Review: React.FC<Step6ReviewProps> = ({ data }) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="font-['Ovo',_serif] text-[22px] sm:text-[26px] lg:text-[28px] text-[#1f1f1f] mb-4 sm:mb-6">Review Your Profile</h2>

      <div className="bg-[#f8f6f2] p-4 sm:p-6 rounded-lg">
        <h3 className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f] mb-3 sm:mb-4">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
          <div>
            <span className="text-[#767472]">Full Name:</span>
            <span className="ml-2 text-[#1f1f1f]">{data.fullName || 'Not provided'}</span>
          </div>
          <div>
            <span className="text-[#767472]">Phone:</span>
            <span className="ml-2 text-[#1f1f1f]">{data.phone || 'Not provided'}</span>
          </div>
        </div>
      </div>

      <div className="bg-[#f8f6f2] p-4 sm:p-6 rounded-lg">
        <h3 className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f] mb-3 sm:mb-4">
          Professional Details
        </h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-[#767472]">Business Name:</span>
            <span className="ml-2 text-[#1f1f1f]">{data.businessName || 'Not provided'}</span>
          </div>
          <div>
            <span className="text-[#767472]">Years of Experience:</span>
            <span className="ml-2 text-[#1f1f1f]">{data.yearsOfExperience} years</span>
          </div>
          <div>
            <span className="text-[#767472]">Bio:</span>
            <p className="mt-1 text-[#1f1f1f]">{data.bio || 'Not provided'}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#f8f6f2] p-4 sm:p-6 rounded-lg">
        <h3 className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f] mb-3 sm:mb-4">
          Services & Specialties
        </h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-[#767472]">Specialties:</span>
            <span className="ml-2 text-[#1f1f1f] break-words">
              {data.specialties.length > 0 ? data.specialties.join(', ') : 'None selected'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#f8f6f2] p-4 sm:p-6 rounded-lg">
        <h3 className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f] mb-3 sm:mb-4">
          Education & Credentials
        </h3>
        <div className="space-y-3 sm:space-y-4">
          {data.education.length > 0 && (
            <div>
              <span className="text-[#767472] font-semibold text-sm">Education:</span>
              {data.education.map((edu, index) => (
                <div key={index} className="mt-2 pl-3 sm:pl-4 border-l-2 border-[#c6a664]">
                  <p className="text-[#1f1f1f] text-sm">
                    <span className="font-semibold">
                      {edu.degree.length > 0 ? edu.degree.join(', ') : 'Degree not specified'}
                    </span>
                    {edu.institution && ` - ${edu.institution}`}
                    {edu.year && ` (${edu.year})`}
                  </p>
                </div>
              ))}
            </div>
          )}
          {data.certifications.length > 0 && (
            <div>
              <span className="text-[#767472] font-semibold text-sm">Certifications:</span>
              {data.certifications.map((cert, index) => (
                <div key={index} className="mt-2 pl-3 sm:pl-4">
                  <p className="text-[#1f1f1f] text-sm">
                    <span className="font-semibold">
                      {cert.name.length > 0 ? cert.name.join(', ') : 'Certification not specified'}
                    </span>
                    {cert.issuer && ` - ${cert.issuer}`}
                    {cert.year && ` (${cert.year})`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#f8f6f2] p-4 sm:p-6 rounded-lg">
        <h3 className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f] mb-3 sm:mb-4">
          Practice Location
        </h3>
        <div className="text-sm">
          <p className="text-[#1f1f1f]">
            {data.address && `${data.address}, `}
            {data.city && `${data.city}, `}
            {data.state} {data.zipCode}
          </p>
          {data.website && (
            <p className="mt-2 break-all">
              <span className="text-[#767472]">Website:</span>
              <span className="ml-2 text-[#1f1f1f]">{data.website}</span>
            </p>
          )}
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 p-3 sm:p-4 rounded-lg">
        <p className="text-green-800 font-['Open_Sans'] text-sm">
          ✓ Your profile is ready to be submitted! Click "Complete Profile" below to save your
          information.
        </p>
      </div>
    </div>
  );
};

export default Step6Review;
